import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Row, Col, CustomInput, Button } from 'reactstrap';
import history from '~/services/history';

import PageHeader from '~/components/Common/PageHeader';
import SearchBox from '~/components/SearchBox';
import Pagination from '~/components/Pagination';
import DeleteModal from '~/components/Modals/DeleteModal';
import Loading from '~/components/Loading';

import { setSearch } from '~/store/modules/search/actions';
import useBatchSelect from '~/hooks/useBatchSelect';
import api from '~/services/api';

import CourierBlacklistCard from './CourierBlacklistCard';

const PARTNER_MAP = {};

const normalizePartnerCode = code =>
  String(code || '')
    .trim()
    .toUpperCase();
const partnerLabel = code => {
  const c = normalizePartnerCode(code);
  return (
    PARTNER_MAP[c] ||
    c.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase())
  );
};

const BlockedCouriers = () => {
  const dispatch = useDispatch();

  const LIST_URL = '/administrator/blacklist';
  const getURL = useMemo(() => LIST_URL, [LIST_URL]);

  const PAGE_TITLE = 'Entregadores Bloqueados';
  const OBJECT_NAME = 'courier_blacklist';

  useEffect(() => {
    document.title = `${PAGE_TITLE} | Viddy`;
  }, []);

  const roles = useSelector(({ user }) => user.profile.roles);
  const isAllowedToBatch = useMemo(
    () => roles.some(r => r.slug !== 'stockist'),
    [roles]
  );

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);

  const searchQuery = useSelector(({ search }) => search.query);
  const [partnerFilter, setPartnerFilter] = useState('');
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    dispatch(setSearch(''));
  }, [dispatch]);

  const currentPage =
    pagination?.current_page && pagination.current_page > 1
      ? pagination.current_page
      : 1;

  // monta params e RESPEITA overrides (inclui page:1 quando necessário)
  const buildParams = useCallback(
    (overrides = {}) => {
      const base = {
        page: currentPage,
        query: searchQuery || null,
        delivery_method: partnerFilter || null,
      };
      const merged = { ...base, ...overrides };

      // limpa valores “falsy” que não queremos enviar
      const params = { ...merged };
      if (!params.query) delete params.query;
      if (!params.delivery_method) delete params.delivery_method;
      if (!params.page || params.page === 1) delete params.page; // backend assume 1

      return params;
    },
    [currentPage, searchQuery, partnerFilter]
  );

  const params = useMemo(() => buildParams(), [buildParams]);

  const setData = useCallback(data => {
    const rows = (data && data[OBJECT_NAME]) || [];
    const meta = data?.meta || null;
    setItems(rows);
    setPagination(meta);
  }, []);

  const fetchList = useCallback(
    async overrides => {
      setLoading(true);
      try {
        const { data } = await api.get(LIST_URL, {
          params: buildParams(overrides),
        });
        setData(data);
      } finally {
        setLoading(false);
      }
    },
    [LIST_URL, buildParams, setData]
  );

  // primeira carga
  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Também adiciona parceiros que apareçam nos itens carregados (sem duplicar)
  useEffect(() => {
    if (!items?.length) return;
    const onPage = Array.from(
      new Set(
        items.map(i => normalizePartnerCode(i?.delivery_method)).filter(Boolean)
      )
    );
    if (!onPage.length) return;

    const mapped = onPage.map(code => ({
      value: code,
      label: partnerLabel(code),
    }));
    setPartners(prev => {
      const existing = new Set(prev.map(p => p.value));
      const merged = [...prev, ...mapped.filter(m => !existing.has(m.value))];
      return merged.sort((a, b) => a.label.localeCompare(b.label));
    });
  }, [items]);

  // trocar filtro: reseta para página 1 e já consulta
  const handlePartnerChange = useCallback(
    value => {
      const v = normalizePartnerCode(value);
      setPartnerFilter(v);
      // força ir para a 1ª página do novo conjunto filtrado
      fetchList({
        page: 1,
        delivery_method: v || null,
        query: searchQuery || null,
      });
    },
    [fetchList, searchQuery]
  );

  const { selectedItems: selectedCouriers, handleSelect } =
    useBatchSelect(items);

  const [openModal, setOpenModal] = useState(false);
  const [courierToBeDeleted, setCourierToBeDeleted] = useState(null);

  return (
    <div className="page-content">
      <Container className="container new-layout wide">
        <PageHeader
          pageTitle="Entregadores Bloqueados"
          responsivePosition="start"
          desktopPosition="start"
        />
        <Row className="w-100 mx-0 mb-2 mb-lg-4">
          <Col lg={12} className="px-0 mb-4 my-lg-0 d-lg-none">
            <SearchBox
              key={`sb-${partnerFilter}`}
              url={getURL}
              query={searchQuery}
              setQuery={q => dispatch(setSearch({ query: q }))}
              params={params}
              setData={setData}
              setLoading={setLoading}
            />
          </Col>
        </Row>
        <Row className="w-100 mx-0 mb-4">
          <Col className="px-0 order-2 order-lg-1" lg={10} align="start">
            <div className="d-flex flex-row align-items-baseline">
              <span className="text-left mt-4 mt-lg-0 d-inline text-muted font-size-14 mr-2">
                Transportadora:
              </span>
              <div className="d-flex align-items-center">
                <CustomInput
                  type="select"
                  id="partnerFilter"
                  value={partnerFilter}
                  onChange={e => handlePartnerChange(e.target.value)}
                >
                  <option value="">Todos</option>
                  {partners.map(p => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </CustomInput>
              </div>
            </div>
          </Col>
          <Col
            className="px-0 order-1 order-lg-2 mb-2 mt-2 mt-lg-0 mb-lg-0 pl-lg-3"
            lg={2}
            align="end"
          >
            <Button
              className="new-layout-btn main btn btn-primary waves-effect waves-light w-100 text-nowrap"
              color="primary"
              onClick={() => history.push(`entregadores-bloqueados/registrar`)}
            >
              <i className="fas fa-user-plus font-size-16 align-middle mr-2" />
              Novo Entregador
            </Button>
          </Col>
        </Row>

        {loading ? (
          <Loading />
        ) : (
          <>
            {items?.length ? (
              <>
                {/* Cabeçalho (desktop) */}
                <Row className="w-100 mx-0 mb-4 d-none d-lg-flex px-3">
                  <Col xs={4}>
                    <div className="new-layout-table-column-label">
                      Entregador
                    </div>
                  </Col>
                  <Col xs={2}>
                    <div className="new-layout-table-column-label">
                      Telefone
                    </div>
                  </Col>
                  <Col xs={2}>
                    <div className="new-layout-table-column-label">
                      Parceiro
                    </div>
                  </Col>
                  <Col xs={3}>
                    <div className="new-layout-table-column-label">Veículo</div>
                  </Col>
                  <Col xs={1} align="center">
                    <div className="new-layout-table-column-label">Ações</div>
                  </Col>
                </Row>

                {/* Linhas */}
                {items.map(courier => {
                  const isSelected = selectedCouriers.some(
                    s => s.id === courier.id
                  );
                  return (
                    <Row className="w-100 mx-0" key={courier.id}>
                      <Col className="px-0" xs={12}>
                        <CourierBlacklistCard
                          key={courier.id}
                          courier={courier}
                          showCheckbox={isAllowedToBatch}
                          isSelected={isSelected}
                          onSelect={handleSelect}
                          onDelete={item => {
                            setCourierToBeDeleted(item);
                            setOpenModal(true);
                          }}
                        />
                      </Col>
                    </Row>
                  );
                })}

                {/* Paginação */}
                <Row className="w-100 mx-0 mb-4">
                  <Col className="px-0" align="end">
                    {pagination && (
                      <div className="mb-3">
                        <Pagination
                          paginationMeta={pagination}
                          url={LIST_URL}
                          setData={setData}
                          setLoading={setLoading}
                          params={params}
                        />
                      </div>
                    )}
                  </Col>
                </Row>
              </>
            ) : (
              <h3>Nenhum entregador bloqueado encontrado.</h3>
            )}
          </>
        )}
      </Container>

      <DeleteModal
        show={openModal}
        setShow={setOpenModal}
        deleteUrl={`/administrator/blacklist/${courierToBeDeleted?.id}`}
        getUrl={LIST_URL}
        params={params}
        setData={setData}
        itemsArray={items}
        setItemToBeDeleted={setCourierToBeDeleted}
        headerText="Deseja mesmo excluir o entregador da blacklist?"
        bodyText="Essa ação irá remover o entregador da lista de bloqueio."
        itemName={courierToBeDeleted?.name}
        successMsg="Entregador removido com sucesso!"
      />
    </div>
  );
};

export default BlockedCouriers;
